import classNames from "classnames";
import type { CSSProperties } from "react";

import {
  usePanelNavigationStore,
  setSharedActiveSection,
} from "../../../store";
import type { PanelEntry } from "../../../utils/extractPanelEntries";
import styles from "./Panel.module.scss";

interface PanelProps {
  entries: PanelEntry[];
  align?: "left" | "right" | "center";
}

/**
 * Sidebar panel with group anchor links for navigation.
 * @param entries - Panel entries to display
 * @param align - Alignment of the panel
 * @returns JSX element
 */
export function Panel({ entries, align = "left" }: PanelProps) {
  const activeSection = usePanelNavigationStore(s => s.activeSection);

  if (entries.length === 0) return null;

  function handleClick(panelId: string) {
    setSharedActiveSection(panelId);
  }

  return (
    <nav
      className={classNames(
        styles.panel,
        align === "right" ? styles.right : styles.left,
      )}>
      <span className={styles.header}>Sections</span>
      <span className={styles.divider}></span>
      <ul className={styles.list}>
        <li key="ALL" className={styles.item}>
          <button
            type="button"
            className={classNames(
              styles.anchor,
              activeSection === null && styles.active,
            )}
            onClick={() => setSharedActiveSection(null)}>
            All
          </button>
        </li>
        {entries.map(({ panelId, text, level }) => {
          const isActive = activeSection === panelId;

          const itemStyle: CSSProperties = {
            paddingLeft: `${level * 12}px`,
          };

          return (
            <li key={panelId} className={styles.item} style={itemStyle}>
              <button
                type="button"
                className={classNames(styles.anchor, isActive && styles.active)}
                onClick={() => handleClick(panelId)}>
                {text}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
